import { datadogRum } from '@datadog/browser-rum';
import * as React from 'react';
import { styled } from 'styled-components';

import { CaretDown, CaretUp, Logout } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useAuthActions } from '@creditornot/wolt-auth/react';

import { oAuth2Service } from 'common/auth/authService';
import { AUTH_LOGIN_FLOW } from 'common/auth/config';
import { MIDNIGHT_BLUE_TOKEN } from 'common/constants';
import useUserInfo from 'common/hooks/useUserInfo';
import { media } from 'common/media';
import { useAuthState } from 'common/utils/authenticatorUtils';

import CommonAvatar from './CommonAvatar';
import CompanySelector from './CompanySelector';
import { useCompaniesByUserId, useCompanyByCompanyId, useCurrentCompany } from './hooks';
import ListOfCompany from './ListOfCompany';

const MANAGE_CONSENTS_ENABLED = false;

type Props = {
  className?: string;
  onNameClick?: () => void;
};

const StyledPopover = styled(Popover)`
  --borderRadius: 8px;
  --cb-color-bg: ${MIDNIGHT_BLUE_TOKEN.COLOR.BG_POPUP};
  min-width: calc(100vw - 32px);
  left: 13px !important;

  @media ${media.sm} {
    min-width: 300px;
  }
`;

const StyledMenuActionItem = styled(MenuActionItem)`
  &:hover:not([disabled]) {
    background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER} !important;
`;
const Container = styled.div`
  ${typographyCss.body}
  display: grid;
  grid-template-columns: 48px auto;
  grid-template-rows: repeat(2, auto);
  color: ${color.fixedWhite};
  column-gap: 20px;
  padding-bottom: 16px;
  padding-top: 8px;

  button {
    color: ${color.fixedWhite};
    svg {
      fill: ${color.fixedWhite};
    }

    &:hover {
      color: ${color.fixedWhite};
    }
  }
`;

const MenuContainer = styled.div`
  padding: 8px 16px;
  border-radius: var(--borderRadius);

  div > [data-cb-menu-item] {
    color: ${color.fixedWhite};
  }
`;

const Divider = styled.div`
  border-radius: var(--borderRadius);
  width: 100%;
  height: 1px;
  background: ${MIDNIGHT_BLUE_TOKEN.COLOR.ACTIVE};
`;

const IconContainer = styled.div`
  display: flex;
  fill: currentColor;
  width: 12px;
  margin-left: 8px;
`;

const StyledCommonAvatar = styled(CommonAvatar)`
  grid-row: 1 / span 2;
  position: relative;
`;

const CompanyName = styled.span`
  ${typographyCss.caption}
  grid-row: 2;
  grid-column: 2;
  opacity: 0.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  display: block !important;
`;
const Name = styled.div`
  font-weight: 600;
  grid-row: 1;
  grid-column: 2;
`;

const CompanyLogo = styled.div`
  ${typographyCss.caption}
  border-radius: 50%;
  background-color: ${color.bgFillBrand};
  position: absolute;
  right: -2px;
  padding: 2px;
  width: 24px;
  box-sizing: border-box;
  height: 24px;
  top: -2px;
  text-align: center;
  text-transform: uppercase;
`;

const Menu: React.FC<{ email?: string }> = () => {
  const { user } = useAuthState();
  const companiesByUser = useCompaniesByUserId({ userId: user?.id });
  const companiesByUserLength = companiesByUser.data?.length ?? 0;
  const shouldShowCompanySelector = companiesByUserLength > 5;
  const shouldShowCompanyList = companiesByUserLength > 1;
  const { logout } = useAuthActions();

  const handleLogout = () => {
    if (!confirm('Are you sure you want to log out?')) {
      return;
    }

    if (AUTH_LOGIN_FLOW.data !== 'oauth2') {
      logout();
    } else {
      window.location.href = oAuth2Service.getLogoutUri();
    }
  };

  const handleManageConsents = () => {
    throw new Error('WIP');
  };

  const getCompanySelector = () => {
    if (companiesByUser.isPending || shouldShowCompanySelector) {
      return <CompanySelector />;
    }

    if (shouldShowCompanyList) {
      return <ListOfCompany />;
    }

    return null;
  };

  return (
    <MenuContainer>
      <MenuItemGroup>
        {getCompanySelector()}
        {shouldShowCompanyList ? <Divider /> : null}

        <StyledMenuActionItem variant="grey" icon={<Logout />} onClick={handleLogout}>
          <span>Logout</span>
        </StyledMenuActionItem>

        {MANAGE_CONSENTS_ENABLED && (
          <StyledMenuActionItem variant="grey" onClick={handleManageConsents}>
            Manage Consents
          </StyledMenuActionItem>
        )}
      </MenuItemGroup>
    </MenuContainer>
  );
};

const UserInfoRoot = styled.div`
  cursor: pointer;
`;

const UserInfoWithMenu: React.FC<Props> = ({ className, onNameClick }) => {
  const { user } = useAuthState();
  const [open, setOpen] = React.useState(false);

  const { currentCompany } = useCurrentCompany();
  const companiesByUser = useCompaniesByUserId({ userId: user?.id });
  const company = companiesByUser.data?.find(
    ({ companyId }) => currentCompany.data?.id && currentCompany.data?.id === companyId,
  );

  const query = useCompanyByCompanyId({ companyId: company?.companyId });
  const userInfoQuery = useUserInfo();
  const firstName = userInfoQuery.data?.firstName ?? user?.name?.first_name ?? '';
  const lastName = userInfoQuery.data?.lastName ?? user?.name?.last_name ?? '';
  const initialFirstName = firstName?.[0]?.toLocaleUpperCase() ?? '-';
  const initialLastName = lastName?.[0]?.toLocaleUpperCase() ?? '-';

  React.useEffect(() => {
    datadogRum.setGlobalContextProperty('companyId', currentCompany.data?.id ?? 'N/A');
  }, [currentCompany.data?.id]);

  if (!user) {
    return null;
  }

  return (
    <StyledPopover
      aria-label="user info"
      offset={16}
      matchReferenceWidth
      renderArrow={false}
      placement="top"
      content={<Menu email={user.email} />}
      onOpenStart={() => setOpen(true)}
      onCloseStart={() => setOpen(false)}
    >
      {({ refProps }) => (
        <UserInfoRoot
          {...refProps}
          onClick={!onNameClick ? refProps.onClick : onNameClick}
          data-testid="user-info-button"
        >
          <Container className={className}>
            <StyledCommonAvatar>
              {query.isPending ? null : (
                <CompanyLogo>{query.data?.name?.substring(0, 2)}</CompanyLogo>
              )}
              <span>{initialFirstName}</span>
              <span>{initialLastName}</span>
            </StyledCommonAvatar>
            <Name className="info">
              {[firstName, lastName].filter(Boolean).join(' ')}
              <IconContainer>{open ? <CaretUp /> : <CaretDown />}</IconContainer>
            </Name>

            <CompanyName title={query.data?.name} className="info">
              {query.data?.name ?? '-'}
            </CompanyName>
          </Container>
        </UserInfoRoot>
      )}
    </StyledPopover>
  );
};

export default UserInfoWithMenu;
